<script>
  // Top-level app component — wires Header + ModeToggle + TabBar + Workspace.
  // Boots by:
  //   1. Health-checking the server (badge state)
  //   2. Fetching the keys list to seed/reconcile tabs
  //   3. Rendering the active tab's workspace
  import { keysApi, healthApi, jobsApi } from "./lib/api/client.js";
  import {
    tabs, getActiveTab, getMode, reconcileTabsFromKeys, setActiveTabId,
    openTabForKey, createTab, openJobSse, closeJobSse,
  } from "./lib/stores/app.svelte.js";
  import Header        from "./lib/components/Header.svelte";
  import ModeToggle    from "./lib/components/ModeToggle.svelte";
  import TabBar        from "./lib/components/TabBar.svelte";
  import KeyWorkspace  from "./lib/components/KeyWorkspace.svelte";
  import AuthModal     from "./lib/components/AuthModal.svelte";

  let authed = $state(false);
  let health  = $state(null);
  let keys    = $state([]);
  let keysSummary = $state(null);
  let bootError = $state(null);

  async function bootAfterAuth() {
    authed = true;
    // Health first — quickest call, sets the badge.
    try { health = await healthApi.check(); }
    catch (err) { health = { ok: false, error: err.message }; }
    // Then keys — drives tab list.
    try {
      const data = await keysApi.list();
      keys = data.keys || [];
      keysSummary = data.summary || null;
      if (keys.length > 0) {
        if (tabs.length === 1 && tabs[0].keyId === "default") {
          tabs.splice(0, 1, createTab(keys[0]));
          setActiveTabId(keys[0].id);
        }
        reconcileTabsFromKeys(keys);
      }
    } catch (err) {
      bootError = `Không load được keys: ${err.message}`;
    }
    // After tabs + keys are settled, honor a #/job/<id> in the URL by
    // loading that job into the active tab. Lets the user share a
    // direct link to a specific pipeline run.
    await _hydrateJobFromHash();
  }

  // Parse `location.hash` for `#/job/<id>` and load that job into the
  // active tab. Re-runs on hashchange so back/forward navigation works.
  async function _hydrateJobFromHash() {
    if (typeof window === "undefined") return;
    const m = window.location.hash.match(/^#\/job\/([\w-]+)$/);
    if (!m) return;
    const jobId = m[1];
    const tab = getActiveTab();
    if (!tab) return;
    if (tab.jobId === jobId) return; // already showing it
    try {
      const job = await jobsApi.get(jobId);
      closeJobSse(tab);
      tab.jobId = jobId;
      tab.job = job;
      tab.error = job.error || null;
      tab.files = [];
      tab.filePreviews = [];
      tab.startedAt = job.created_at || null;
      const stage = job.stage;
      const terminal = stage === "done" || stage === "error" ||
                       stage === "interrupted" || stage === "cancelled";
      if (stage === "done") tab.view = "succeeded";
      else if (stage === "error" || stage === "interrupted") tab.view = "failed";
      else if (stage === "cancelled") tab.view = "cancelled";
      else tab.view = "tracking";
      tab.activePanel = "generator";
      if (!terminal) openJobSse(tab, jobId);
    } catch (err) {
      console.warn(`Không tìm được job ${jobId}:`, err.message);
    }
  }

  // Back / forward navigation between job hashes.
  $effect(() => {
    if (!authed || typeof window === "undefined") return;
    const handler = () => { _hydrateJobFromHash(); };
    window.addEventListener("hashchange", handler);
    window.addEventListener("popstate", handler);
    return () => {
      window.removeEventListener("hashchange", handler);
      window.removeEventListener("popstate", handler);
    };
  });

  // Reactive: which tab is shown right now? $derived re-evaluates when
  // tabs[] or activeTabId mutates.
  const activeTab = $derived(getActiveTab());
  const mode      = $derived(getMode());

  // No onMount boot — AuthModal calls onAuthed which triggers bootAfterAuth().
</script>

{#if !authed}
  <AuthModal onAuthed={bootAfterAuth} />
{:else}
  <div class="app-shell">
    <Header {health} {keysSummary} {keys}
            onKeyAdded={(k) => { keys = [...keys, k]; }}
            onKeyDeleted={(id) => { keys = keys.filter(k => k.id !== id); }} />

    <ModeToggle />

    {#if mode === "multi"}
      <TabBar {keys} onKeyAdded={(k) => { keys = [...keys, k]; }} />
    {/if}

    {#if bootError}
      <div class="boot-error">{bootError}</div>
    {/if}

    <main class="workspace">
      {#if activeTab}
        {#key activeTab.keyId}
          <KeyWorkspace tab={activeTab} {keys} />
        {/key}
      {:else}
        <p class="muted">Chưa có tab nào mở. Thêm 1 key qua header.</p>
      {/if}
    </main>
  </div>
{/if}

<style>
  .app-shell {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  .workspace {
    flex: 1;
    width: 100%;
    max-width: min(1400px, 96vw);
    margin: 0 auto;
    padding: 16px 20px 40px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .boot-error {
    margin: 16px;
    padding: 12px 14px;
    background: rgba(248, 113, 113, 0.08);
    border: 1px solid rgba(248, 113, 113, 0.3);
    border-radius: var(--radius-sm);
    color: #ff8585;
    font-size: 0.85rem;
  }

  .muted {
    color: var(--muted);
    text-align: center;
    padding: 40px 20px;
  }
</style>
